import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const benefits = [
  {
    icon: "fas fa-key",
    title: "Wide choice of villas, townhouses, and apartments",
  },
  {
    icon: "fas fa-chart-line",
    title: "Strong long-term investment and rental potential",
  },
  {
    icon: "fas fa-map-marker-alt",
    title: "Access to premium residential communities",
  },
  {
    icon: "fas fa-home",
    title: "Homes suited to every budget and lifestyle",
  },
];

const buyingSteps = [
  ["Understand Your Requirements", "We discuss your budget, preferred locations, and property goals."],
  ["Curated Property Selection", "You receive tailored house options that match your criteria."],
  ["Property Viewings", "We arrange visits so you can explore each house in detail."],
  ["Negotiation and Closing", "We guide you through offers, paperwork, and final ownership transfer."],
];

const faqs = [
  {
    icon: "fas fa-home",
    question: "What types of houses are available for sale in Abu Dhabi?",
    answer: "You can find all different types of houses in Abu Dhabi, such as luxury villas, townhouses, apartments, and family homes in gated communities.",
  },
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy houses in Abu Dhabi?",
    answer: "Yes. Abu Dhabi allows foreign nationals to buy houses for investment purposes.",
  },
  {
    icon: "fas fa-handshake",
    question: "How does NV Real Estate help me find the right house?",
    answer: "We listen to you carefully. Based on your requirements, we filter all available houses for sale and recommend the best ones to you. We also help you explore them, make your choice, complete the transaction, and get access to your property.",
  },
  {
    icon: "fas fa-coins",
    question: "Are there financing options available for buying houses?",
    answer: "Yes, many banks in the UAE offer mortgage options for both residents and non-residents, subject to eligibility criteria.",
  },
  {
    icon: "fas fa-chart-line",
    question: "Is Abu Dhabi a good place to invest in houses?",
    answer: "Yes. Abu Dhabi is a great place to invest in houses, thanks to its stable market, investor-friendly regulations, and strong long-term returns.",
  },
  {
    icon: "fas fa-file-invoice-dollar",
    question: "What additional costs should I pay for buying a house?",
    answer: "In addition to the property price, you should budget for registration fees, agent fees, and potential maintenance costs.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Abu Dhabi Houses for Sale", item: "https://nvrealestate.ae/abu-dhabi-houses-for-sale" },
  ],
};

function AbuDhabiHousesForSale() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Abu Dhabi Houses for Sale – Premium Homes UAE</title>
        <meta
          name="description"
          content="Searching for Abu Dhabi houses for sale? NV Real Estate can help you buy the best house matching your budget, requirements, and expectations. Let us connect now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content="Abu Dhabi Houses for Sale – Find Your Perfect Home Today" />
        <meta property="og:description" content="Find the right Abu Dhabi house with a curated search, private viewings, and expert support from offer to handover." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nvrealestate.ae/abu-dhabi-houses-for-sale" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/abu-dhabi-houses-for-sale/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi home buying specialists</span>
              <h1 id="villa-page-title">Abu Dhabi Houses for Sale – Find Your Perfect Home Today</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore properties
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img src="/images/seo/abu-dhabi-houses-for-sale/02.png" alt="Houses and residential communities in Abu Dhabi" />
              <div className="villa-intro__note">
                <strong>Local expertise</strong>
                <span>For UAE residents and international buyers</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Abu Dhabi Houses for Sale</h2>
              <p>
                NV Real Estate is the most dependable realtor in Abu Dhabi to help you buy the
                best house for your needs. We have access to all{" "}
                <Link to="/archive-properties/apartments-to-buy-abu-dhabi/">high-quality apartments</Link>,
                townhouses, and villas developed by trustworthy developers, so we can recommend the
                residential property best suited to your lifestyle, budget, and investment goals.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="villa-benefits-title">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">The market</span>
              <h2 id="villa-benefits-title">Discover the Best Properties to Buy in Abu Dhabi</h2>
              <p>
                Abu Dhabi's real estate market is diverse and investor-friendly, offering housing
                for both residents and international buyers. Choose luxury waterfront villas,
                family-friendly townhouses, or ready-to-move apartments, all in communities built
                around schools, healthcare, retail, and leisure &mdash; ideal for comfortable
                living and long-term investment.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {benefits.map((benefit, index) => (
                <article className="villa-benefit-card" key={benefit.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={benefit.icon} /></span>
                  <h3>{benefit.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">Your buying journey</span>
              <h2 id="villa-process-title">A Buying Process That Works for You</h2>
              <p>Buying property should feel clear and manageable. Our process is to simplify every step.</p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {buyingSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
              <p className="villa-process__closing">This structured approach ensures you make informed decisions with confidence.</p>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Buy Your House from NV Real Estate?</h2>
                <p>
                  NV Real Estate is among the most trusted names for residential property purchase.
                  Our clients applaud us for our professionalism, transparency, and client-focused
                  approach. We prioritize your goals and ensure every step of your property journey
                  is handled with precision and care.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">Call us now to get the best houses for sale in Abu Dhabi.</p>
              </div>
              <ul>
                <li>Experienced real estate professionals with deep market knowledge</li>
                <li>Strong relationships with leading developers and property owners</li>
                <li>Honest, data-driven advice to support informed decisions</li>
                <li>End-to-end assistance from property selection to final transaction</li>
                <li>Commitment to delivering long-term value and client satisfaction</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your home search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`house-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`house-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Homes</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Offer to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.</p>
          </div>
        </section>
      </main>

      <CTA />
    </>
  );
}

export default AbuDhabiHousesForSale;